import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, Link } from 'react-router-dom';
import { Phone, Instagram, Facebook, MapPin, Calendar, Menu, X } from 'lucide-react';

const Layout = ({ children }: { children: React.ReactNode }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePos({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <div className="min-h-screen bg-cream font-sans text-dark overflow-x-hidden selection:bg-rose/30">
      {/* Custom Ultra Cursor */}
      <motion.div
        className="fixed top-0 left-0 w-8 h-8 rounded-full border border-sunset z-[9999] pointer-events-none hidden lg:block"
        animate={{ x: mousePos.x - 16, y: mousePos.y - 16 }}
        transition={{ type: "spring", stiffness: 150, damping: 15 }}
      />
      <motion.div
        className="fixed top-0 left-0 w-2 h-2 bg-royal rounded-full z-[9999] pointer-events-none hidden lg:block"
        animate={{ x: mousePos.x - 4, y: mousePos.y - 4 }}
        transition={{ type: "spring", stiffness: 250, damping: 20 }}
      />
      <header
        className={`fixed w-full z-50 transition-all duration-700 ${isScrolled || location.pathname !== '/' ? 'py-4 bg-white/90 backdrop-blur-2xl shadow-premium' : 'py-8 bg-transparent'
          }`}
      >
        <div className="container mx-auto px-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3 group">
            <motion.div
              whileHover={{ scale: 1.1, rotate: 5 }}
              className="relative"
            >
              <img src="/images/moyhe hair branding logo.jpg" alt="Moyhe Logo" className="h-12 w-12 rounded-full object-cover border-2 border-white/50 shadow-premium" />
              <div className="absolute -inset-1 rounded-full bg-gradient-vibrant opacity-30 animate-pulse-slow blur-sm"></div>
            </motion.div>
            <div className={`text-2xl font-serif font-bold tracking-wider transition-colors duration-500 ${isScrolled || location.pathname !== '/' ? 'text-dark' : 'text-white'
              }`}>
              Moyhe <span className="text-gradient-vibrant italic">Hair</span>
            </div>
          </Link>

          <div className="flex items-center gap-6">
            <nav className="hidden lg:flex items-center space-x-10">
              {['Home', 'Services', 'Gallery', 'Blog', 'About', 'Contact'].map((item) => (
                <Link
                  key={item}
                  to={item === 'Home' ? '/' : `/${item.toLowerCase()}`}
                  className={`relative font-bold tracking-[0.2em] uppercase text-[10px] hover:text-sunset transition-all duration-500 group ${isScrolled || location.pathname !== '/' ? 'text-dark' : 'text-white'
                    }`}
                >
                  {item}
                  <span className="absolute -bottom-2 left-0 w-0 h-[2px] bg-gradient-vibrant transition-all duration-500 group-hover:w-full"></span>
                </Link>
              ))}
            </nav>

            <div className="flex items-center gap-4">
              <a
                href="tel:17046417227"
                className="hidden md:inline-flex items-center gap-2 bg-gradient-vibrant text-white px-10 py-3 rounded-full font-bold text-[10px] shadow-sunset hover:shadow-rose transform hover:-translate-y-1 transition-all duration-500 uppercase tracking-widest"
              >
                <Phone size={14} /> Call Us
              </a>

              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className={`lg:hidden p-2 transition-colors duration-500 ${isScrolled || location.pathname !== '/' ? 'text-dark' : 'text-white'}`}
                aria-label="Toggle menu"
              >
                {isMenuOpen ? <X size={32} /> : <Menu size={32} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-white border-t border-gray-100 overflow-hidden"
            >
              <div className="container mx-auto px-4 py-8 flex flex-col space-y-6">
                {['Home', 'Services', 'Gallery', 'Blog', 'About', 'Contact'].map((item) => (
                  <Link
                    key={item}
                    to={item === 'Home' ? '/' : `/${item.toLowerCase()}`}
                    className="text-2xl font-serif text-dark hover:text-gold transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item}
                  </Link>
                ))}
                <a
                  href="tel:17046417227"
                  className="bg-gold text-dark py-4 rounded-xl font-bold text-center text-lg flex items-center justify-center gap-3 shadow-premium"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <Phone size={20} /> Call for Appointment
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <main>
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            {children}
          </motion.div>
        </AnimatePresence>
      </main>

      <footer className="bg-dark text-white py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <img src="/images/moyhe hair branding logo.jpg" alt="Moyhe Logo" className="h-10 w-10 rounded-full border border-gray-600" />
                <span className="text-2xl font-serif font-bold">Moyhe Hair</span>
              </div>
              <p className="text-gray-400 leading-relaxed">
                Your premier destination for authentic African hair braiding in Charlotte. We combine tradition with modern style.
              </p>
            </div>
            <div>
              <h4 className="text-xl font-serif font-bold mb-6">Contact Us</h4>
              <ul className="space-y-4 text-gray-400">
                <li className="flex items-center gap-3"><Phone size={18} className="text-gold" /> (704) 641-7227</li>
                <li className="flex items-center gap-3"><MapPin size={18} className="text-gold" /> Charlotte, NC</li>
                <li className="flex items-center gap-3"><Calendar size={18} className="text-gold" /> Mon-Sat: 9am - 7pm</li>
              </ul>
            </div>
            <div>
              <h4 className="text-xl font-serif font-bold mb-6">Follow Us</h4>
              <div className="flex gap-4">
                <a href="#" className="bg-white/10 p-3 rounded-full hover:bg-sunset hover:text-white transition-colors"><Instagram size={20} /></a>
                <a href="#" className="bg-white/10 p-3 rounded-full hover:bg-royal hover:text-white transition-colors"><Facebook size={20} /></a>
                <a
                  href="https://vt.tiktok.com/ZSaP8PKkS/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white/10 p-3 rounded-full hover:bg-rose hover:text-white transition-colors"
                  aria-label="TikTok"
                >
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.03 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.28-2.26.74-4.63 2.58-5.91 1.64-1.15 3.7-1.49 5.66-1.02v4.53c-.31-.19-.71-.24-1.07-.26-.67-.03-1.4.03-2.03.35-.8.41-1.41 1.18-1.48 2.08-.1 1.28.82 2.51 2.1 2.78.36.08.73.1 1.1.1 1.65-.05 3.14-1.31 3.4-2.94.02-.15.03-.3.03-.45V0c-.01 0 .01 0 0 .02z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-500 text-sm">
            <p>&copy; 2025 Moyhe Hair Braiding. All Rights Reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
